import * as Sentry from "@sentry/browser";
import axios, { type AxiosInstance } from "axios";
import {
  v2RequestSnakeCaseTransformer,
  v2ResponseCamelCaseTransformer,
} from "mws-utils/lib/caseTransformers/axiosCaseTransformers";
import { dateTransformer } from "mws-utils/lib/dateTransformer";

import { platformKey } from "~/constants/headerKeys";
import getServerSentry from "~/server/helpers/getServerSentry";
import { getAnalyticsHeaders } from "~/utils/analytics";

export default defineNuxtPlugin({
  dependsOn: ["platform", "amplify"],
  setup: () => {
    // we are adding an adapter to axios to make it work with fetch so that it works in the Cloudflare workers
    axios.defaults.adapter = axiosFetchAdapter;
    axios.defaults.timeout = 15000;

    setAxiosInterceptors(axios);
  },
});

function setAxiosInterceptors(axios: AxiosInstance) {
  const auth = useAuth();
  const platformStore = usePlatformStore();
  const nuxtApp = useNuxtApp();
  const runtimeConfig = useRuntimeConfig();

  axios.defaults.transformResponse = [dateTransformer];
  axios.interceptors.request.use(async (req) => {
    const transformedReq = v2RequestSnakeCaseTransformer(req);

    transformedReq.headers.Authorization = await auth.getToken();
    transformedReq.headers[platformKey] =
      platformStore.tryGetPlatformConfiguration?.platformId;

    const analyticsContext = getAnalyticsHeaders(nuxtApp);
    let anonymousId = "";
    if (import.meta.client && nuxtApp.$segment.browser.value) {
      const user = await nuxtApp.$segment.browser.value.user();
      anonymousId = user?.anonymousId() ?? "";
    }
    analyticsContext.properties.anonymousId = anonymousId;

    transformedReq.headers = {
      ...transformedReq.headers,
      trackingContext: JSON.stringify(analyticsContext),
    };

    return transformedReq;
  });

  axios.interceptors.response.use(v2ResponseCamelCaseTransformer, (error) => {
    if (error.code === "ETIMEDOUT") {
      const errorRequest = (error.request || {}) as Partial<Request>;

      const errorHeaders: Record<string, any> = {};
      errorRequest.headers?.forEach((value, key) => {
        if (key.toLowerCase() === "authorization") {
          value = "[REDACTED]";
        }

        errorHeaders[key] = value;
      });

      const errorExtra = {
        url: errorRequest.url || "UNKNOWN_URL",
        method: errorRequest.method || "UNKNOWN_METHOD",
        headers: errorHeaders,
      };

      if (import.meta.client) {
        Sentry.captureException(error, { extra: errorExtra });
      } else {
        const serverSentry = getServerSentry(runtimeConfig);
        serverSentry?.setExtras(errorExtra);
        serverSentry?.captureException(error);
      }
    }

    const err = error;
    if ("response" in err) {
      err.response = v2ResponseCamelCaseTransformer(err.response);
    }

    if (err.response?.status === 404) {
      throw createError({
        statusCode: 404,
        statusMessage: "404 page not found",
        message: "404 page not found",
      });
    }

    throw err;
  });
}
